/**
 * Pure helpers for the Customer 360 page (labels, money sums, filtering,
 * sorting). No React, no I/O — unit-tested directly.
 *
 * Money stays an integer-Rial digit string end to end: sums use BigInt and
 * never pass through a JS number.
 */

export type Tone = 'neutral' | 'info' | 'success' | 'warn' | 'danger';

/** Persian labels — the customer endpoint returns raw enum keys, never expose them. */
const QUOTE_STATUS_LABEL: Readonly<Record<string, string>> = {
  draft: 'پیش‌نویس',
  sent: 'ارسال‌شده',
  accepted: 'پذیرفته‌شده',
  rejected: 'رد شده',
  expired: 'منقضی',
};

const ORDER_STATUS_LABEL: Readonly<Record<string, string>> = {
  draft: 'پیش‌نویس',
  confirmed: 'تأیید شده',
  partially_fulfilled: 'تحویل جزئی',
  fulfilled: 'تحویل کامل',
  cancelled: 'لغو شده',
};

const QUOTE_TONE: Readonly<Record<string, Tone>> = {
  draft: 'neutral',
  sent: 'info',
  accepted: 'success',
  rejected: 'danger',
  expired: 'neutral',
};

const ORDER_TONE: Readonly<Record<string, Tone>> = {
  draft: 'neutral',
  confirmed: 'info',
  partially_fulfilled: 'warn',
  fulfilled: 'success',
  cancelled: 'neutral',
};

export const UNKNOWN_STATUS_LABEL = 'نامشخص';

export function quoteStatusLabel(status: string): string {
  return QUOTE_STATUS_LABEL[status] ?? UNKNOWN_STATUS_LABEL;
}
export function quoteStatusTone(status: string): Tone {
  return QUOTE_TONE[status] ?? 'neutral';
}
export function orderStatusLabel(status: string): string {
  return ORDER_STATUS_LABEL[status] ?? UNKNOWN_STATUS_LABEL;
}
export function orderStatusTone(status: string): Tone {
  return ORDER_TONE[status] ?? 'neutral';
}

/**
 * The opening-receivable read model returns at most this many rows
 * (`listOpeningImportItemsForParty` … LIMIT 500). A list that reaches the cap may
 * be truncated, so its count and sum must never be presented as totals (ERP-266:
 * no capped-list totals).
 */
export const OPENING_AR_READ_CAP = 500;

export function isAtReadCap(count: number, cap: number = OPENING_AR_READ_CAP): boolean {
  return count >= cap;
}

const DIGITS = /^\d+$/;

function toBig(digits: string): bigint | null {
  return DIGITS.test(digits) ? BigInt(digits) : null;
}

/** Exact sum of integer-Rial digit strings; entries that are not digits are skipped. */
export function sumRial(values: readonly string[]): string {
  let total = 0n;
  for (const value of values) {
    const big = toBig(value);
    if (big !== null) total += big;
  }
  return total.toString();
}

/** Ascending comparison of integer-Rial digit strings without number conversion. */
export function compareRial(a: string, b: string): number {
  const x = toBig(a) ?? 0n;
  const y = toBig(b) ?? 0n;
  return x === y ? 0 : x < y ? -1 : 1;
}

/** Case-insensitive "all words appear somewhere" match across the given fields. */
export function matchesQuery(
  fields: readonly (string | null | undefined)[],
  query: string,
): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = fields.join(' ').toLowerCase();
  return words.every((word) => haystack.includes(word));
}

export type DocSort = 'default' | 'amountDesc' | 'amountAsc' | 'ref';

export const DOC_SORT_OPTIONS: readonly { readonly value: DocSort; readonly label: string }[] = [
  { value: 'default', label: 'ترتیب پیش‌فرض' },
  { value: 'amountDesc', label: 'مبلغ: زیاد به کم' },
  { value: 'amountAsc', label: 'مبلغ: کم به زیاد' },
  { value: 'ref', label: 'شماره' },
];

/** Stable sort; `default` keeps the order the server returned. */
export function sortDocs<T>(
  rows: readonly T[],
  sort: DocSort,
  amountOf: (row: T) => string,
  refOf: (row: T) => string,
): readonly T[] {
  if (sort === 'default') return rows;
  const indexed = rows.map((row, index) => ({ row, index }));
  indexed.sort((a, b) => {
    let result = 0;
    if (sort === 'amountDesc') result = compareRial(amountOf(b.row), amountOf(a.row));
    else if (sort === 'amountAsc') result = compareRial(amountOf(a.row), amountOf(b.row));
    else result = refOf(a.row).localeCompare(refOf(b.row), 'en', { numeric: true });
    return result !== 0 ? result : a.index - b.index;
  });
  return indexed.map((entry) => entry.row);
}

/** `YYYY-MM-DD` business dates compare correctly as strings. */
export function isBeforeDay(date: string | null | undefined, today: string): boolean {
  return Boolean(date) && (date as string).slice(0, 10) < today;
}

/** Up to two initials of a name (ZWNJ-joined, Persian-friendly). */
export function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => Array.from(word)[0] ?? '')
    .join('‌');
}

export type Customer360Tab =
  | 'overview'
  | 'contacts'
  | 'communications'
  | 'activities'
  | 'deals'
  | 'quotes'
  | 'orders'
  | 'invoicesAr'
  | 'notes'
  | 'changeHistory';

export const CUSTOMER_TABS: readonly { readonly id: Customer360Tab; readonly label: string }[] = [
  { id: 'overview', label: 'نمای کلی' },
  { id: 'contacts', label: 'مخاطبان' },
  { id: 'communications', label: 'ارتباطات' },
  { id: 'activities', label: 'فعالیت‌ها' },
  { id: 'deals', label: 'معاملات' },
  { id: 'quotes', label: 'پیش‌فاکتورها' },
  { id: 'orders', label: 'سفارش و تحویل' },
  { id: 'invoicesAr', label: 'فاکتور و دریافتنی' },
  { id: 'notes', label: 'یادداشت‌ها' },
  { id: 'changeHistory', label: 'تاریخچه تغییرات' },
];

export function parseTab(value: string | null): Customer360Tab {
  return CUSTOMER_TABS.find((tab) => tab.id === value)?.id ?? 'overview';
}

export interface UnavailableCopy {
  readonly title: string;
  readonly description: string;
  /** Module list that is useful in the meantime (not filtered by customer). */
  readonly link?: { readonly to: 'crm/activities' | 'crm/leads'; readonly label: string };
}

/**
 * The API reports these tabs as `unavailable` with an English technical
 * reason; the page shows this Persian explanation instead. Keyed by tab, not
 * by reason text, so a reworded backend message cannot leak to users.
 */
export const UNAVAILABLE_COPY: Readonly<Partial<Record<Customer360Tab, UnavailableCopy>>> = {};

export const GENERIC_UNAVAILABLE_COPY: UnavailableCopy = {
  title: 'این بخش هنوز در دسترس نیست',
  description: 'منبع دادهٔ معتبری برای این بخش وجود ندارد.',
};

export const PHONE_KIND_LABEL: Readonly<Record<string, string>> = {
  landline: 'ثابت',
  mobile: 'موبایل',
  other: 'سایر',
};
